import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  QAccountAccessSection,
  QBox,
  QButtonGroup,
  QDivider,
  QPropertiesPanel,
  QStack,
  QHeading,
  QTogglePropertiesPanelButton,
  QButton,
  useFeatureFlag,
} from '@qualio/ui-components';
import { TemplateDetail } from '../../types';
import { LabelValueDisplay } from './utils';
import PropertiesModal from './PropertiesModal';
import { useTemplateActions } from '../../hooks/useTemplateActions';
import { BaseQueryKey } from '../../hooks/useTemplate';
import { useCompany } from '../../hooks/useCompany';
import { fileDownloadOptionsLabel, resolveFileDownloadUserGroups } from '../../utils/templateFormUtil';
import { getPeriodicReviewDisplayString, getReviewApprovalLimitDisplayString } from '../../utils/textUtil';
import { useTags } from '../../hooks/useTags';
import { DefaultTagsDisplay } from './DefaultTagsDisplay';
export const TemplateDetailsProperty: React.FC<{ data: TemplateDetail }> = ({ data }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { canEditProperties, canManageAccountAccess } = useTemplateActions(data);
  const featureFlags = useFeatureFlag();
  const templateDefaultTags = Boolean(featureFlags['template-default-tags']);
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);
  const { data: tags = [] } = useTags();
  const { data: company } = useCompany(enterpriseAccounts);
  const isParentAccount = enterpriseAccounts && !!company?.is_parent;
  const sharedCompanyIds = data.shared_company_ids;
  const isOwnTemplate = sharedCompanyIds !== undefined;
  const queryClient = useQueryClient();
  return (
    <>
      <QPropertiesPanel openOnMount>
        <QBox w="350px">
          <QStack isInline justifyContent="space-between" spacing="2" alignItems="center">
            <QHeading>{t('template.properties.title')}</QHeading>
            <QButtonGroup>
              {canEditProperties && (
                <QButton data-cy="template-edit-properties-button" variant="ghost" onClick={() => setIsOpen(true)}>
                  {t('common.buttons.edit')}
                </QButton>
              )}
              <QTogglePropertiesPanelButton />
            </QButtonGroup>
          </QStack>
          <QStack spacing="4" mt="16px">
            <LabelValueDisplay label={t('template.properties.fields.title')} dataCy={'name'}>
              {data.name}
            </LabelValueDisplay>
            <LabelValueDisplay label={t('template.properties.fields.prefix')} dataCy={'prefix'}>
              {data.prefix}
            </LabelValueDisplay>
            <LabelValueDisplay
              label={t('template.properties.fields.eligibleForTraining')}
              dataCy={'training-available'}
            >
              {data.training_available ? t('common.boolean.yes') : t('common.boolean.no')}
            </LabelValueDisplay>
            <LabelValueDisplay
              label={t('template.properties.fields.requiredTrainingOnEachVersion')}
              dataCy={'training-required'}
            >
              {data.training_required ? t('common.boolean.yes') : t('common.boolean.no')}
            </LabelValueDisplay>
            <LabelValueDisplay label={t('template.properties.fields.reviewPeriod')} dataCy={'review-period'}>
              {getPeriodicReviewDisplayString(data.document_review_period, true)}
            </LabelValueDisplay>
            <LabelValueDisplay label={t('template.properties.fields.reviewLimitShort')} dataCy={'review-limit'}>
              {getReviewApprovalLimitDisplayString(data.document_review_due_in_days, true)}
            </LabelValueDisplay>
            <LabelValueDisplay label={t('template.properties.fields.approvalLimitShort')} dataCy={'approval-limit'}>
              {getReviewApprovalLimitDisplayString(data.document_approval_due_in_days, true)}
            </LabelValueDisplay>
            <LabelValueDisplay
              label={t('template.properties.fields.downloadPermissions')}
              dataCy={'download-permissions'}
            >
              {fileDownloadOptionsLabel[resolveFileDownloadUserGroups(data.file_download_user_groups)]}
            </LabelValueDisplay>
            <LabelValueDisplay
              label={t('template.properties.fields.effectiveOnApprovalShort')}
              dataCy={'effective-on-approval'}
            >
              {data.document_effective_on_approval ? t('common.boolean.yes') : t('common.boolean.no')}
            </LabelValueDisplay>
            <LabelValueDisplay label={t('template.properties.fields.showSectionTitles')} dataCy={'sections-visible'}>
              {data.document_text_sections_visible ? t('common.boolean.yes') : t('common.boolean.no')}
            </LabelValueDisplay>
            {templateDefaultTags && (
              <DefaultTagsDisplay
                label={t('template.properties.fields.defaultTags')}
                tagIds={data.tag_ids}
                tags={tags}
              />
            )}
          </QStack>
          {isParentAccount && isOwnTemplate && (
            <>
              <QDivider mt="6" mb="6" />
              <QAccountAccessSection
                entityType="doc_template"
                entityMatrixId={data.template_uuid}
                sharedCompanyIds={sharedCompanyIds}
                canManage={canManageAccountAccess}
                onShared={() => queryClient.invalidateQueries([BaseQueryKey])}
              />
            </>
          )}
        </QBox>
      </QPropertiesPanel>
      {canEditProperties && <PropertiesModal isOpen={isOpen} setIsOpen={setIsOpen} data={data} />}
    </>
  );
};
